import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Form,
  InputGroup,
} from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { FaCopy } from 'react-icons/fa';
import * as Yup from 'yup';
import Header from '~/Components/Header/Header';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { extrairMensagemErro } from '~/Utils/Helpers';
import {
  listarGruposEmpresaDifLiq,
  listarContasDisponiveisDifLiq,
  listarContasDifLiq,
  listarFamiliasAtivasDifLiq,
  incluirContasDifLiq,
  editarDeflacaoDifLiq,
  removerContaDifLiq,
} from '~/Services/Service';
import GridDisponiveis from './components/GridDisponiveis';
import GridContasDifLiq from './components/GridContasDifLiq';
import ModalConfirmaPropagacao from './components/ModalConfirmaPropagacao';
import PainelCopiar from './components/PainelCopiar';
import DesktopOnlyGuard from './components/DesktopOnlyGuard';

const filtroSchema = Yup.object({
  codigoGrupoEmpresa: Yup.number()
    .typeError('Selecione um Grupo de Empresa.')
    .required('Selecione um Grupo de Empresa.')
    .positive()
    .integer(),
  codigoContaInicio: Yup.number().nullable().integer().min(1).max(999999),
  codigoContaFim: Yup.number()
    .nullable()
    .integer()
    .min(Yup.ref('codigoContaInicio'), 'Deve ser >= conta início'),
  tipoConta: Yup.string().nullable().length(1),
});

const TIPOS_CONTA = [
  { value: '6', label: '6-Interna' },
  { value: '8', label: '8-Funcionário/Prest.Serviço' },
];

const ITENS_POR_PAGINA = 20;

const ordenarLista = (lista, campo, direcao) =>
  lista.slice().sort((a, b) => {
    const dir = direcao === 'asc' ? 1 : -1;
    let va = a[campo];
    let vb = b[campo];
    if (va == null) va = '';
    if (vb == null) vb = '';
    if (typeof va === 'string') return dir * va.toLowerCase().localeCompare(String(vb).toLowerCase());
    return dir * (va - vb);
  });

const ContasDificilLiquidacao = () => {
  // --- Dados de domínio ---
  const [gruposEmpresa, setGruposEmpresa] = useState([]);
  const [familiasDisponiveis, setFamiliasDisponiveis] = useState([]);

  // --- Filtros ---
  const [geId, setGeId] = useState(null);
  const [familiaFiltroId, setFamiliaFiltroId] = useState(null);
  const [contaInicio, setContaInicio] = useState('');
  const [contaFim, setContaFim] = useState('');
  const [tipoConta, setTipoConta] = useState(null);
  const [errosFiltro, setErrosFiltro] = useState({});

  // --- Grids ---
  const [contasDisponiveis, setContasDisponiveis] = useState([]);
  const [contasConfig, setContasConfig] = useState([]);
  const [loadingDisp, setLoadingDisp] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [paginaDisp, setPaginaDisp] = useState(1);
  const [paginaConfig, setPaginaConfig] = useState(1);

  // --- Seleção no grid disponíveis ---
  const [selecionados, setSelecionados] = useState(new Set());
  const [ordDisp, setOrdDisp] = useState({ campo: 'codigo_conta', direcao: 'asc' });
  const [ordConfig, setOrdConfig] = useState({ campo: 'conta', direcao: 'asc' });

  // --- Inclusão ---
  const [deflacao, setDeflacao] = useState('');
  const [familiaInclusaoId, setFamiliaInclusaoId] = useState(null);
  const [incluindo, setIncluindo] = useState(false);
  const [modalPropagacao, setModalPropagacao] = useState({ show: false, familias: [], pendente: null });

  // --- Edição inline ---
  const [editandoLinha, setEditandoLinha] = useState(null);

  // --- Remover confirmação ---
  const [confirmacaoRemover, setConfirmacaoRemover] = useState({ show: false, item: null });

  // --- Copiar configurações ---
  const [showCopiar, setShowCopiar] = useState(false);

  // --- Carga inicial: lista de GEs ---
  useEffect(() => {
    let ativo = true;
    listarGruposEmpresaDifLiq()
      .then((res) => { if (ativo) setGruposEmpresa(res || []); })
      .catch((err) => { if (ativo) toast.error(extrairMensagemErro(err)); });
    return () => { ativo = false; };
  }, []);

  // Quando GE muda: recarrega famílias do filtro e limpa seleções
  useEffect(() => {
    setFamiliaFiltroId(null);
    setFamiliaInclusaoId(null);
    setFamiliasDisponiveis([]);
    setSelecionados(new Set());
    setContasDisponiveis([]);
    setContasConfig([]);
    setPaginaDisp(1);
    setPaginaConfig(1);
    if (!geId) return undefined;
    let ativo = true;
    listarFamiliasAtivasDifLiq(geId)
      .then((res) => { if (ativo) setFamiliasDisponiveis(res || []); })
      .catch((err) => { if (ativo) toast.error(extrairMensagemErro(err)); });
    return () => { ativo = false; };
  }, [geId]);

  const carregarDisponiveis = useCallback(async (params) => {
    setLoadingDisp(true);
    setSelecionados(new Set());
    try {
      const res = await listarContasDisponiveisDifLiq(params);
      setContasDisponiveis(res || []);
      setPaginaDisp(1);
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setLoadingDisp(false);
    }
  }, []);

  const carregarConfig = useCallback(async (params) => {
    setLoadingConfig(true);
    try {
      const res = await listarContasDifLiq(params);
      setContasConfig(res || []);
      setPaginaConfig(1);
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  const handlePesquisar = async () => {
    try {
      await filtroSchema.validate(
        {
          codigoGrupoEmpresa: geId,
          codigoContaInicio: contaInicio !== '' ? Number(contaInicio) : null,
          codigoContaFim: contaFim !== '' ? Number(contaFim) : null,
          tipoConta,
        },
        { abortEarly: false }
      );
      setErrosFiltro({});
    } catch (e) {
      const mapa = {};
      e.inner.forEach((er) => { mapa[er.path] = er.message; });
      setErrosFiltro(mapa);
      return;
    }

    const paramsDisp = {
      codigoGrupoEmpresa: geId,
      codigoContaInicio: contaInicio !== '' ? Number(contaInicio) : undefined,
      codigoContaFim: contaFim !== '' ? Number(contaFim) : undefined,
      tipoConta: tipoConta || undefined,
    };
    const paramsConf = {
      codigoGrupoEmpresa: geId,
      familia: familiaFiltroId || undefined,
    };
    carregarDisponiveis(paramsDisp);
    carregarConfig(paramsConf);
  };

  const handleLimpar = () => {
    setGeId(null);
    setFamiliaFiltroId(null);
    setContaInicio('');
    setContaFim('');
    setTipoConta(null);
    setErrosFiltro({});
    setContasDisponiveis([]);
    setContasConfig([]);
    setSelecionados(new Set());
  };

  // --- Seleção grid disponíveis ---
  const handleToggle = (cod) => {
    setSelecionados((prev) => {
      const next = new Set(prev);
      if (next.has(cod)) next.delete(cod);
      else next.add(cod);
      return next;
    });
  };

  const handleToggleTodos = (checked, contasPagina) => {
    if (checked) {
      setSelecionados((prev) => {
        const next = new Set(prev);
        contasPagina.forEach((c) => next.add(c.codigo_conta));
        return next;
      });
    } else {
      setSelecionados((prev) => {
        const next = new Set(prev);
        contasPagina.forEach((c) => next.delete(c.codigo_conta));
        return next;
      });
    }
  };

  // --- Incluir ---
  const executarInclusao = useCallback(async ({ familia, confirmarPropagacao }) => {
    if (selecionados.size === 0) {
      toast.warn('Selecione ao menos uma conta antes de incluir.');
      return;
    }
    if (!geId) {
      toast.warn('Selecione um Grupo de Empresa antes de incluir.');
      return;
    }
    if (deflacao === '' || deflacao === null || deflacao === undefined) {
      toast.warn('Informe o valor de deflação antes de incluir.');
      return;
    }
    setIncluindo(true);
    try {
      const res = await incluirContasDifLiq({
        codigo_grupo_empresa: geId,
        familia: familia || null,
        deflacao: parseFloat(String(deflacao).replace(',', '.')),
        contas: Array.from(selecionados),
        confirmar_propagacao: confirmarPropagacao || false,
      });
      toast.success(res.mensagem || `${res.linhas_criadas} registro(s) incluído(s).`);
      setSelecionados(new Set());
      setDeflacao('');
      carregarConfig({ codigoGrupoEmpresa: geId, familia: familiaFiltroId || undefined });
      carregarDisponiveis({
        codigoGrupoEmpresa: geId,
        codigoContaInicio: contaInicio !== '' ? Number(contaInicio) : undefined,
        codigoContaFim: contaFim !== '' ? Number(contaFim) : undefined,
        tipoConta: tipoConta || undefined,
      });
    } catch (err) {
      const msg = err && err.message ? err.message : 'Não foi possível concluir a operação. Tente novamente.';
      toast.error(msg);
    } finally {
      setIncluindo(false);
    }
  }, [selecionados, geId, deflacao, familiaFiltroId, contaInicio, contaFim, tipoConta, carregarConfig, carregarDisponiveis]);

  const handleIncluir = async () => {
    if (!geId) {
      toast.warn('Selecione um Grupo de Empresa antes de incluir.');
      return;
    }
    if (selecionados.size === 0) {
      toast.warn('Selecione ao menos uma conta antes de incluir.');
      return;
    }
    if (!familiaInclusaoId) {
      // Família em branco → propagar para todas: buscar lista e exibir modal
      const familias = familiasDisponiveis.length > 0 ? familiasDisponiveis : [];
      if (familias.length === 0) {
        try {
          const res = await listarFamiliasAtivasDifLiq(geId);
          setModalPropagacao({ show: true, familias: res || [], pendente: { familia: null, confirmarPropagacao: true } });
        } catch (err) {
          toast.error(extrairMensagemErro(err));
        }
      } else {
        setModalPropagacao({ show: true, familias, pendente: { familia: null, confirmarPropagacao: true } });
      }
      return;
    }
    executarInclusao({ familia: familiaInclusaoId, confirmarPropagacao: false });
  };

  const handleConfirmarPropagacao = (confirmado) => {
    const pendente = modalPropagacao.pendente;
    setModalPropagacao({ show: false, familias: [], pendente: null });
    if (confirmado && pendente) {
      executarInclusao(pendente);
    }
  };

  // --- Edição inline ---
  const handleIniciarEdicao = (item, chave) => {
    setEditandoLinha({
      chave,
      conta: item.conta,
      codigoGrupoEmpresa: item.codigo_grupo_empresa,
      familia: item.familia,
      deflacaoOriginal: Number(item.deflacao),
      deflacaoEditada: String(item.deflacao),
    });
  };

  const handleCancelarEdicao = () => setEditandoLinha(null);

  const handleDeflacaoChange = (val) => {
    setEditandoLinha((prev) => ({ ...prev, deflacaoEditada: val }));
  };

  const handleSalvarDeflacao = async () => {
    if (!editandoLinha) return;
    const val = parseFloat(String(editandoLinha.deflacaoEditada).replace(',', '.'));
    if (isNaN(val) || val < 0 || val > 100) {
      toast.warn('Deflação deve ser um número entre 0 e 100.');
      return;
    }
    try {
      const res = await editarDeflacaoDifLiq(editandoLinha.conta, {
        codigo_grupo_empresa: editandoLinha.codigoGrupoEmpresa,
        conta: editandoLinha.conta,
        familia: editandoLinha.familia,
        deflacao: val,
      });
      toast.success(res.mensagem || 'Deflação atualizada.');
      setEditandoLinha(null);
      carregarConfig({ codigoGrupoEmpresa: geId, familia: familiaFiltroId || undefined });
    } catch (err) {
      const msg = err && err.message ? err.message : 'Não foi possível concluir a operação. Tente novamente.';
      toast.error(msg);
    }
  };

  // --- Remover ---
  const handleRemover = (item) => setConfirmacaoRemover({ show: true, item });

  const handleConfirmarRemover = async (confirmado) => {
    const item = confirmacaoRemover.item;
    setConfirmacaoRemover({ show: false, item: null });
    if (!confirmado || !item) return;
    try {
      const res = await removerContaDifLiq(item.conta, {
        codigo_grupo_empresa: item.codigo_grupo_empresa,
        conta: item.conta,
        familia: item.familia,
      });
      toast.success(res.mensagem || 'Registro removido.');
      carregarConfig({ codigoGrupoEmpresa: geId, familia: familiaFiltroId || undefined });
    } catch (err) {
      const msg = err && err.message ? err.message : 'Não foi possível concluir a operação. Tente novamente.';
      toast.error(msg);
    }
  };

  // --- Ordenação ---
  const handleOrdenarDisp = (campo) => {
    setPaginaDisp(1);
    setOrdDisp((prev) => ({
      campo,
      direcao: prev.campo === campo && prev.direcao === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleOrdenarConfig = (campo) => {
    setPaginaConfig(1);
    setOrdConfig((prev) => ({
      campo,
      direcao: prev.campo === campo && prev.direcao === 'asc' ? 'desc' : 'asc',
    }));
  };

  const contasDispOrd = useMemo(
    () => ordenarLista(contasDisponiveis, ordDisp.campo, ordDisp.direcao),
    [contasDisponiveis, ordDisp]
  );

  const contasConfigOrd = useMemo(
    () => ordenarLista(contasConfig, ordConfig.campo, ordConfig.direcao),
    [contasConfig, ordConfig]
  );

  const contasDispPag = useMemo(() => {
    const inicio = (paginaDisp - 1) * ITENS_POR_PAGINA;
    return contasDispOrd.slice(inicio, inicio + ITENS_POR_PAGINA);
  }, [contasDispOrd, paginaDisp]);

  const contasConfigPag = useMemo(() => {
    const inicio = (paginaConfig - 1) * ITENS_POR_PAGINA;
    return contasConfigOrd.slice(inicio, inicio + ITENS_POR_PAGINA);
  }, [contasConfigOrd, paginaConfig]);

  useEffect(() => {
    const totalPaginas = Math.max(1, Math.ceil(contasDispOrd.length / ITENS_POR_PAGINA));
    if (paginaDisp > totalPaginas) setPaginaDisp(totalPaginas);
  }, [contasDispOrd, paginaDisp]);

  useEffect(() => {
    const totalPaginas = Math.max(1, Math.ceil(contasConfigOrd.length / ITENS_POR_PAGINA));
    if (paginaConfig > totalPaginas) setPaginaConfig(totalPaginas);
  }, [contasConfigOrd, paginaConfig]);

  const opcoesGE = useMemo(
    () => gruposEmpresa.map((g) => ({
      value: g.codigo_grupo_empresa,
      label: `${g.codigo_grupo_empresa} — ${g.nome || g.apelido || ''}`,
    })),
    [gruposEmpresa]
  );

  const opcoesFamilias = useMemo(
    () => familiasDisponiveis.map((f) => ({
      value: f.codigo_familia,
      label: `${f.codigo_familia} — ${f.nome_da_familia}`,
    })),
    [familiasDisponiveis]
  );

  return (
    <DesktopOnlyGuard>
      <Container fluid>
      <Header title="Contas de Difícil Liquidação" />

      {/* Card de filtros */}
      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Grupo de Empresa <span className="text-danger">*</span></Form.Label>
                <Select
                  styles={customStyles}
                  components={{ DropdownIndicator }}
                  options={opcoesGE}
                  value={opcoesGE.find((o) => o.value === geId) || null}
                  onChange={(opt) => setGeId(opt ? opt.value : null)}
                  placeholder="Selecione o GE..."
                  noOptionsMessage={() => 'Nenhum GE disponível'}
                  isClearable
                />
                {errosFiltro.codigoGrupoEmpresa && (
                  <Form.Text className="text-danger">{errosFiltro.codigoGrupoEmpresa}</Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Família (filtro grid configuradas)</Form.Label>
                <Select
                  styles={customStyles}
                  components={{ DropdownIndicator }}
                  options={opcoesFamilias}
                  value={opcoesFamilias.find((o) => o.value === familiaFiltroId) || null}
                  onChange={(opt) => setFamiliaFiltroId(opt ? opt.value : null)}
                  placeholder="Todas as famílias"
                  noOptionsMessage={() => geId ? 'Nenhuma família encontrada' : 'Selecione o GE'}
                  isDisabled={!geId}
                  isClearable
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Tipo de Conta</Form.Label>
                <Select
                  styles={customStyles}
                  components={{ DropdownIndicator }}
                  options={TIPOS_CONTA}
                  value={TIPOS_CONTA.find((o) => o.value === tipoConta) || null}
                  onChange={(opt) => setTipoConta(opt ? opt.value : null)}
                  placeholder="Padrão (6 e 8)"
                  isClearable
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="align-items-end">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Conta de</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Início"
                  value={contaInicio}
                  onChange={(e) => setContaInicio(e.target.value)}
                  min={1}
                />
                {errosFiltro.codigoContaInicio && (
                  <Form.Text className="text-danger">{errosFiltro.codigoContaInicio}</Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Conta até</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Fim"
                  value={contaFim}
                  onChange={(e) => setContaFim(e.target.value)}
                  min={1}
                />
                {errosFiltro.codigoContaFim && (
                  <Form.Text className="text-danger">{errosFiltro.codigoContaFim}</Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={6} className="d-flex justify-content-end">
              <Button variant="outline-secondary" className="mr-2" onClick={handleLimpar}>
                Limpar
              </Button>
              <Button variant="primary" className="mr-2" onClick={handlePesquisar}>
                Pesquisar
              </Button>
              <Button
                variant="outline-info"
                onClick={() => setShowCopiar(true)}
                disabled={!geId}
              >
                <FaCopy className="mr-1" /> Copiar Configurações
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Grids lado a lado */}
      <Row>
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>
              <strong>Disponíveis</strong>
              {selecionados.size > 0 && (
                <span className="text-muted ml-2">({selecionados.size} selecionada(s))</span>
              )}
            </Card.Header>
            <Card.Body className="p-0">
              <GridDisponiveis
                contas={contasDispPag}
                selecionados={selecionados}
                onToggle={handleToggle}
                onToggleTodos={handleToggleTodos}
                ordenacao={ordDisp}
                onOrdenar={handleOrdenarDisp}
                loading={loadingDisp}
                paginaAtual={paginaDisp}
                totalRegistros={contasDispOrd.length}
                itensPorPagina={ITENS_POR_PAGINA}
                onPaginaChange={setPaginaDisp}
              />
            </Card.Body>
            {contasDisponiveis.length > 0 && (
              <Card.Footer>
                <Row className="align-items-center">
                  <Col md={4}>
                    <Form.Group className="mb-0">
                      <Form.Label className="mb-0">Deflação (%)</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min={0}
                          max={100}
                          placeholder="0,00"
                          value={deflacao}
                          onChange={(e) => setDeflacao(e.target.value)}
                        />
                        <InputGroup.Append>
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup.Append>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                  <Col md={5}>
                    <Form.Group className="mb-0">
                      <Form.Label className="mb-0">Família (inclusão)</Form.Label>
                      <Select
                        styles={customStyles}
                        components={{ DropdownIndicator }}
                        options={opcoesFamilias}
                        value={opcoesFamilias.find((o) => o.value === familiaInclusaoId) || null}
                        onChange={(opt) => setFamiliaInclusaoId(opt ? opt.value : null)}
                        placeholder="Todas (propagar)"
                        noOptionsMessage={() => 'Nenhuma família encontrada'}
                        isClearable
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3} className="d-flex align-items-end mt-2 mt-md-0">
                    <Button
                      variant="success"
                      onClick={handleIncluir}
                      disabled={incluindo || selecionados.size === 0}
                      className="w-100"
                    >
                      {incluindo ? 'Incluindo...' : 'Incluir'}
                    </Button>
                  </Col>
                </Row>
              </Card.Footer>
            )}
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>
              <strong>Configuradas</strong>
            </Card.Header>
            <Card.Body className="p-0">
              <GridContasDifLiq
                itens={contasConfigPag}
                editandoLinha={editandoLinha}
                onIniciarEdicao={handleIniciarEdicao}
                onSalvarDeflacao={handleSalvarDeflacao}
                onCancelarEdicao={handleCancelarEdicao}
                onDeflacaoChange={handleDeflacaoChange}
                onRemover={handleRemover}
                ordenacao={ordConfig}
                onOrdenar={handleOrdenarConfig}
                loading={loadingConfig}
                paginaAtual={paginaConfig}
                totalRegistros={contasConfigOrd.length}
                itensPorPagina={ITENS_POR_PAGINA}
                onPaginaChange={setPaginaConfig}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modais */}
      <ModalConfirmaPropagacao
        show={modalPropagacao.show}
        familias={modalPropagacao.familias}
        onConfirmar={() => handleConfirmarPropagacao(true)}
        onCancelar={() => handleConfirmarPropagacao(false)}
      />

      <ModalConfirmacao
        show={confirmacaoRemover.show}
        onClose={handleConfirmarRemover}
        variant="danger"
        title="Remover configuração"
        message={
          confirmacaoRemover.item
            ? `Deseja remover a conta ${confirmacaoRemover.item.conta} — ${confirmacaoRemover.item.apelido_conta}?`
            : 'Deseja remover este registro?'
        }
        labelCancel="Cancelar"
        labelConfirm="Remover"
      />

      <PainelCopiar
        show={showCopiar}
        opcoesGE={opcoesGE}
        onClose={() => setShowCopiar(false)}
        onCopiadoComSucesso={() => {
          carregarConfig({ codigoGrupoEmpresa: geId, familia: familiaFiltroId || undefined });
        }}
      />
      </Container>
    </DesktopOnlyGuard>
  );
};

export default ContasDificilLiquidacao;
